import {
  AccessTime as SleepTimeIcon,
  Timer as ActualSleepTimeIcon,
  Hotel as BedIcon,
  Layers as SleepStagesIcon,
} from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import React, { useState, useMemo } from "react";

import { activityApi } from "../../../../apis";
import {
  TimeRange,
  getDateRangeForTimeRange,
} from "../../../../helpers/dateRangeHelpers";
import {
  buildDataMap,
  buildJsonDataMap,
  buildChartData,
  buildSleepStagesChartData,
} from "../../../../helpers/sleepDataHelpers";
import {
  MetricValuesAggregationRequestDto,
  MetricValuesAggregationRequestDtoIntervalEnum,
  MetricValuesAggregationRequestDtoAggregateTypesEnum,
  MetricValuesAggregationRequestDtoHealthProviderCodeEnum,
  MetricValuesAggregationRequestDtoMetricCodeEnum,
} from "../../../../libs/cellera-sdk";
import { useCelleraTheme } from "../../../../theme/useCelleraTheme";
import { SleepChartCard, TimeRangeSelector } from "./chart-components";

interface Props {
  patientId: string;
}

const SleepChart: React.FC<Props> = ({ patientId }) => {
  const { colors } = useCelleraTheme();
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>("1W");

  const { startDate, endDate } = getDateRangeForTimeRange(
    selectedTimeRange,
    undefined
  );

  /** 📊 Create reusable query configuration */
  const createMetricQuery = (
    queryKey: string,
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum
  ) => ({
    queryKey: [queryKey, patientId, selectedTimeRange],
    queryFn: async () => {
      if (!patientId) return null;
      const interval =
        selectedTimeRange === "6M" || selectedTimeRange === "1Y"
          ? MetricValuesAggregationRequestDtoIntervalEnum.Monthly
          : MetricValuesAggregationRequestDtoIntervalEnum.Daily;

      const request: MetricValuesAggregationRequestDto = {
        metricCode,
        healthProviderCode:
          MetricValuesAggregationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
        interval,
        aggregateTypes: [
          MetricValuesAggregationRequestDtoAggregateTypesEnum.Sum,
        ],
        startDate,
        endDate,
        orderBy: "ASC",
      };

      return activityApi.getMetricValuesAggregatedByPatientId({
        patientId,
        metricValuesAggregationRequestDto: request,
      });
    },
    enabled: !!patientId,
    refetchOnWindowFocus: false,
  });

  /** 📊 Fetch sleep data for all metrics */
  const [
    { data: sleepTimeData, isLoading: sleepTimeLoading },
    { data: actualSleepTimeData, isLoading: actualSleepTimeLoading },
    { data: timeInBedData, isLoading: timeInBedLoading },
    { data: sleepStagesData, isLoading: sleepStagesLoading },
  ] = useQueries({
    queries: [
      createMetricQuery(
        "sleepTimeChart",
        MetricValuesAggregationRequestDtoMetricCodeEnum.SleepTime
      ),
      createMetricQuery(
        "actualSleepTimeChart",
        MetricValuesAggregationRequestDtoMetricCodeEnum.ActualSleepTime
      ),
      createMetricQuery(
        "timeInBedChart",
        MetricValuesAggregationRequestDtoMetricCodeEnum.TimeInBed
      ),
      createMetricQuery(
        "sleepStagesChart",
        MetricValuesAggregationRequestDtoMetricCodeEnum.SleepStages
      ),
    ],
  });

  /** 🧮 Build chart data using helper functions */
  const sleepTimeChartData = useMemo(() => {
    const dataMap = buildDataMap(sleepTimeData?.data);
    return buildChartData(dataMap, selectedTimeRange, null);
  }, [sleepTimeData, selectedTimeRange]);

  const actualSleepTimeChartData = useMemo(() => {
    const dataMap = buildDataMap(actualSleepTimeData?.data);
    return buildChartData(dataMap, selectedTimeRange, null);
  }, [actualSleepTimeData, selectedTimeRange]);

  const timeInBedChartData = useMemo(() => {
    const dataMap = buildDataMap(timeInBedData?.data);
    return buildChartData(dataMap, selectedTimeRange, null);
  }, [timeInBedData, selectedTimeRange]);

  const sleepStagesChartData = useMemo(() => {
    const dataMap = buildJsonDataMap(sleepStagesData?.data);
    return buildSleepStagesChartData(dataMap, selectedTimeRange, null);
  }, [sleepStagesData, selectedTimeRange]);

  const ranges: TimeRange[] = ["1D", "1W", "1M", "6M", "1Y"];
  const isLoading =
    sleepTimeLoading ||
    actualSleepTimeLoading ||
    timeInBedLoading ||
    sleepStagesLoading;

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2">Loading sleep data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Global Controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          p: 2,
          borderRadius: 2,
          border: "1px solid #C6C5D5",
          bgcolor: "#FAFAFA",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <BedIcon sx={{ color: colors.primary.main, fontSize: 24 }} />
          <Typography variant="h6" fontWeight={600}>
            Sleep Metrics
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Time Range Selector */}
          <TimeRangeSelector
            ranges={ranges}
            selectedTimeRange={selectedTimeRange}
            onTimeRangeChange={setSelectedTimeRange}
          />
        </Box>
      </Box>

      {/* Charts Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
        }}
      >
        <SleepChartCard
          title="Sleep Time"
          data={sleepTimeChartData}
          colors={colors}
          selectedTimeRange={selectedTimeRange}
          barColor="#6366F1"
          icon={
            <SleepTimeIcon sx={{ color: colors.primary.main, fontSize: 22 }} />
          }
        />
        <SleepChartCard
          title="Actual Sleep Time"
          data={actualSleepTimeChartData}
          colors={colors}
          selectedTimeRange={selectedTimeRange}
          barColor="#10B981"
          icon={
            <ActualSleepTimeIcon
              sx={{ color: colors.primary.main, fontSize: 22 }}
            />
          }
        />
        <SleepChartCard
          title="Time in Bed"
          data={timeInBedChartData}
          colors={colors}
          selectedTimeRange={selectedTimeRange}
          barColor="#8B5CF6"
          icon={<BedIcon sx={{ color: colors.primary.main, fontSize: 22 }} />}
        />
        <SleepChartCard
          title="Sleep Stages"
          data={sleepStagesChartData}
          colors={colors}
          selectedTimeRange={selectedTimeRange}
          isStacked={true}
          icon={
            <SleepStagesIcon
              sx={{ color: colors.primary.main, fontSize: 22 }}
            />
          }
        />
      </Box>
    </Box>
  );
};

export default SleepChart;
